import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import PageTitle from "../../../layouts/PageTitle";
import { errorAlert } from "../../../components/alert/defaultAlert";
import {
    getWhatsappAttendanceMetrics,
    getWhatsappDropOffMetrics,
    getWhatsappMetrics,
} from "../../../../services/WhatsappService";
import "./WhatsappDashboardPage.css";

const formatNumber = (value) => (value != null ? Number(value).toLocaleString("pt-BR") : "0");

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const RESOLUTION_LINE_COLORS = ["#2563eb", "#0f766e", "#d97706", "#9333ea", "#dc2626", "#0891b2"];


const WhatsappDashboardPage = () => {
    const { t } = useTranslation();
    const [metrics, setMetrics] = useState(null);
    const [dropOffMetrics, setDropOffMetrics] = useState(null);
    const [attendanceMetrics, setAttendanceMetrics] = useState(null);
    const [loading, setLoading] = useState(false);

    const loadDashboard = useCallback(() => {
        setLoading(true);
        Promise.all([
            getWhatsappMetrics(),
            getWhatsappDropOffMetrics(),
            getWhatsappAttendanceMetrics(),
        ])
            .then(([metricsResp, dropOffResp, attendanceResp]) => {
                setMetrics(metricsResp.data);
                setDropOffMetrics(dropOffResp.data);
                setAttendanceMetrics(attendanceResp.data);
            })
            .catch((error) => errorAlert(error))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    if (loading && !metrics && !dropOffMetrics && !attendanceMetrics) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="success" />
                <span className="ms-3 text-muted">{t("loading_data")}</span>
            </div>
        );
    }

    if (!metrics || !dropOffMetrics || !attendanceMetrics) {
        return null;
    }

    const aggregate = metrics.aggregate || {};
    const totalConversations = aggregate.totalConversations || 0;
    const activeConversations = aggregate.activeConversations || 0;
    const resolutionRate = aggregate.resolutionRate || 0;
    const dropOffRate = dropOffMetrics.dropOffRate || 0;
    const resolutionTrend = metrics.resolutionTrend || [];
    const resolutionAssistants = Array.from(
        new Map(resolutionTrend.map((point) => [point.botId, point.botName]))
    ).map(([botId, botName]) => ({
        botId,
        botName,
        dataKey: `assistant_${botId}`,
    }));
    const resolutionTrendByMonth = resolutionTrend.reduce((months, point) => {
        const month = months[point.month] || {
            month: point.month,
            label: new Date(`${point.month}-01T00:00:00`).toLocaleDateString("pt-BR", {
                month: "short",
                year: "numeric",
            }),
        };
        month[`assistant_${point.botId}`] = point.resolutionRate;
        months[point.month] = month;
        return months;
    }, {});
    const resolutionTrendData = Object.values(resolutionTrendByMonth).sort((a, b) => a.month.localeCompare(b.month));
    const kpiCards = [
        {
            label: t("metrics_total_conversations"),
            value: formatNumber(totalConversations),
            detail: `${formatNumber(aggregate.uniqueCustomers30Days)} ${t("metrics_unique_customers_30d").toLowerCase()}`,
            tone: "primary",
            icon: "fa-comments",
        },
        {
            label: t("metrics_active_conversations"),
            value: formatNumber(activeConversations),
            detail: `${formatNumber(attendanceMetrics.openWithoutAttendant)} ${t("open_without_attendant").toLowerCase()}`,
            tone: "teal",
            icon: "fa-bolt",
        },
        {
            label: t("metrics_resolution_rate"),
            value: formatPercent(resolutionRate),
            detail: `${formatNumber(aggregate.closedConversations)} ${t("closed_conversations")}`,
            tone: "success",
            icon: "fa-trophy",
        },
        {
            label: t("dropoff_rate"),
            value: formatPercent(dropOffRate),
            detail: `${formatNumber(dropOffMetrics.possibleDropOffConversations)} ${t("dropoff_possible_dropoffs").toLowerCase()}`,
            tone: "risk",
            icon: "fa-user-clock",
        },
    ];

    return (
        <>
            <PageTitle activeMenu={t("whatsapp_dashboard")} motherMenu={t("chat_bot")} />

            <div className="whatsapp-executive-page">
                <section className="whatsapp-executive-hero whatsapp-executive-hero--compact">
                    <div className="whatsapp-executive-hero__side">
                        <div className="whatsapp-executive-hero__toolbar">
                        <div className="whatsapp-executive-hero__chips">
                            <span className="whatsapp-executive-chip">
                                <i className="fa fa-calendar-day"></i>
                                {t("whatsapp_executive_period", { days: dropOffMetrics.lookbackDays || 30 })}
                            </span>
                            <span className="whatsapp-executive-chip">
                                <i className="fa fa-users"></i>
                                {formatNumber(aggregate.uniqueCustomers30Days)} {t("metrics_unique_customers_30d").toLowerCase()}
                            </span>
                        </div>
                        <button
                            className="whatsapp-executive-refresh"
                            onClick={loadDashboard}
                            disabled={loading}
                        >
                            <i className={`fa fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>
                            <span>{t("whatsapp_refresh_dashboard")}</span>
                        </button>
                        </div>

                    </div>
                </section>

                <section className="whatsapp-kpi-grid">
                    {kpiCards.map((card) => (
                        <KpiCard key={card.label} {...card} />
                    ))}
                </section>

                <section className="whatsapp-panel-grid">
                    <div className="whatsapp-panel">
                        <div className="whatsapp-panel__header">
                            <div className="whatsapp-panel__icon">
                                <i className="fa fa-chart-line"></i>
                            </div>
                            <div>
                                <h3>{t("whatsapp_resolution_trend_title")}</h3>
                                <p>{t("whatsapp_resolution_trend_desc")}</p>
                            </div>
                        </div>
                        <div className="whatsapp-panel__body">
                            {resolutionTrendData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={320}>
                                    <LineChart data={resolutionTrendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="label" tickLine={false} axisLine={false} />
                                        <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} tickLine={false} axisLine={false} />
                                        <Tooltip formatter={(value) => formatPercent(value)} />
                                        <Legend />
                                        {resolutionAssistants.map((assistant, index) => (
                                            <Line
                                                key={assistant.dataKey}
                                                type="monotone"
                                                dataKey={assistant.dataKey}
                                                name={assistant.botName}
                                                stroke={RESOLUTION_LINE_COLORS[index % RESOLUTION_LINE_COLORS.length]}
                                                strokeWidth={2}
                                                dot={{ r: 3 }}
                                                connectNulls
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="whatsapp-empty-panel">
                                    <i className="fa fa-chart-line"></i>
                                    <span>{t("whatsapp_no_resolution_trend")}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

            </div>
        </>
    );
};

const KpiCard = ({ label, value, detail, tone, icon }) => (
    <div className={`whatsapp-kpi-card whatsapp-kpi-card--${tone}`}>
        <div className="whatsapp-kpi-card__icon">
            <i className={`fa ${icon}`}></i>
        </div>
        <div className="whatsapp-kpi-card__content">
            <span>{label}</span>
            <strong>{value}</strong>
            <small>{detail}</small>
        </div>
    </div>
);

export default WhatsappDashboardPage;
